import React, { useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';

export interface CardItem {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface CardSelectorProps {
  /** Items to display as selectable cards */
  items: CardItem[];
  /** Currently selected item ID(s) */
  selected: string | string[];
  /** Called when selection changes */
  onChange: (selected: string | string[]) => void;
  /** Single or multi selection mode */
  mode: 'single' | 'multi';
  /** Number of grid columns (default: items.length or 3, whichever is smaller) */
  columns?: number;
  /** List of item IDs that cannot be toggled (rendered but dimmed) */
  disabled?: string[];
  /** Additional class name for the container */
  className?: string;
  /** When true, auto-focus the tabbable card on mount */
  autoFocus?: boolean;
}

/**
 * CardSelector — Accessible card-based selection component with keyboard navigation.
 *
 * - Single mode: radio group semantics (one selection at a time)
 * - Multi mode: checkbox group semantics (toggle individual items)
 * - Arrow keys navigate between cards (with row-wrapping)
 * - Space/Enter toggle selection
 * - Disabled items are visually dimmed and cannot be toggled
 *
 * @example
 * ```tsx
 * <CardSelector
 *   items={[
 *     { id: 'a', title: 'Option A', description: 'First option' },
 *     { id: 'b', title: 'Option B', description: 'Second option' },
 *   ]}
 *   selected="a"
 *   onChange={(val) => setSelected(val)}
 *   mode="single"
 * />
 * ```
 */
export function CardSelector({
  items,
  selected,
  onChange,
  mode,
  columns,
  disabled: disabledIds = [],
  className,
  autoFocus,
}: CardSelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedSet = new Set(
    Array.isArray(selected) ? selected : selected ? [selected] : []
  );

  const isItemDisabled = (id: string): boolean => {
    return disabledIds.includes(id) || !!items.find((i) => i.id === id)?.disabled;
  };

  const isItemSelected = (id: string): boolean => {
    return selectedSet.has(id);
  };

  const toggleItem = useCallback(
    (id: string) => {
      if (isItemDisabled(id)) return;

      if (mode === 'single') {
        onChange(id);
      } else {
        const arr = Array.isArray(selected) ? selected : selected ? [selected] : [];
        if (arr.includes(id)) {
          onChange(arr.filter((v) => v !== id));
        } else {
          onChange([...arr, id]);
        }
      }
    },
    [mode, selected, onChange, disabledIds, items]
  );

  // Auto-focus the tabbable card on mount when autoFocus is true
  useEffect(() => {
    if (autoFocus && containerRef.current) {
      const tabbable = containerRef.current.querySelector<HTMLElement>('[tabindex="0"]');
      if (tabbable) {
        // Use requestAnimationFrame to ensure DOM is painted before focusing
        requestAnimationFrame(() => tabbable.focus());
      }
    }
  }, [autoFocus]);

  const getEnabledIndices = (): number[] => {
    return items
      .map((item, i) => (isItemDisabled(item.id) ? -1 : i))
      .filter((i) => i >= 0);
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, itemIndex: number) => {
      const enabledIndices = getEnabledIndices();
      const currentEnabledPos = enabledIndices.indexOf(itemIndex);
      if (currentEnabledPos === -1) return;

      const colCount = columns ?? Math.min(items.length, 3);
      let nextEnabledPos = currentEnabledPos;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          nextEnabledPos = (currentEnabledPos + 1) % enabledIndices.length;
          break;
        case 'ArrowLeft':
          e.preventDefault();
          nextEnabledPos =
            (currentEnabledPos - 1 + enabledIndices.length) % enabledIndices.length;
          break;
        case 'ArrowDown':
          e.preventDefault();
          // Jump forward by colCount enabled items
          nextEnabledPos = Math.min(
            currentEnabledPos + colCount,
            enabledIndices.length - 1
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          nextEnabledPos = Math.max(currentEnabledPos - colCount, 0);
          break;
        case 'Tab':
          // Let Tab/Shift+Tab follow normal browser tab order (exit card group)
          return;
        case ' ':
        case 'Enter':
          e.preventDefault();
          toggleItem(items[itemIndex].id);
          return;
        default:
          return;
      }

      // Focus the target card
      const targetIndex = enabledIndices[nextEnabledPos];
      const container = containerRef.current;
      if (container) {
        const cards = container.querySelectorAll<HTMLElement>('[data-card-index]');
        cards[targetIndex]?.focus();
      }
    },
    [items, columns, toggleItem]
  );

  const colCount = columns ?? Math.min(items.length, 3);

  const gridCols: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  // First enabled item gets tabIndex 0 if nothing is selected, otherwise selected items get it
  const enabledIndices = getEnabledIndices();
  const firstSelectedEnabledIndex = items.findIndex(
    (item) => isItemSelected(item.id) && !isItemDisabled(item.id)
  );
  const tabbableIndex =
    firstSelectedEnabledIndex >= 0
      ? firstSelectedEnabledIndex
      : enabledIndices.length > 0
        ? enabledIndices[0]
        : -1;

  return (
    <div
      ref={containerRef}
      role={mode === 'single' ? 'radiogroup' : 'group'}
      className={cn('grid gap-3', gridCols[colCount] || `grid-cols-${colCount}`, className)}
    >
      {items.map((item, index) => {
        const itemDisabled = isItemDisabled(item.id);
        const itemSelected = isItemSelected(item.id);

        return (
          <div
            key={item.id}
            data-card-index={index}
            role={mode === 'single' ? 'radio' : 'checkbox'}
            aria-checked={itemSelected}
            aria-disabled={itemDisabled}
            tabIndex={itemDisabled ? -1 : index === tabbableIndex ? 0 : -1}
            onClick={() => toggleItem(item.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              'relative text-left rounded-xl border-2 p-5 transition-all duration-200 cursor-pointer select-none',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              itemSelected && !itemDisabled &&
                'border-primary-500 bg-primary-50 shadow-md',
              itemSelected && itemDisabled &&
                'border-primary-500 bg-primary-50 shadow-md cursor-not-allowed',
              !itemSelected && !itemDisabled &&
                'border-border bg-background hover:border-border hover:shadow-sm',
              !itemSelected && itemDisabled &&
                'border-border bg-muted opacity-60 cursor-not-allowed',
              itemDisabled && 'cursor-not-allowed'
            )}
          >
            <div className="flex items-start gap-3">
              {item.icon && (
                <div
                  className={cn(
                    'p-2 rounded-lg flex-shrink-0',
                    itemSelected && !itemDisabled
                      ? 'bg-primary-100 text-primary-700'
                      : itemSelected && itemDisabled
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-muted text-muted-foreground'
                  )}
                >
                  {item.icon}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className={cn(
                  'font-semibold text-sm',
                  itemSelected && !itemDisabled ? 'text-gray-900' :
                  itemSelected && itemDisabled ? 'text-gray-900' :
                  'text-foreground'
                )}>
                  {item.title}
                </h3>
                {item.description && (
                  <p className={cn(
                    'text-xs mt-1',
                    itemSelected && !itemDisabled ? 'text-gray-600' :
                    itemSelected && itemDisabled ? 'text-gray-600' :
                    'text-muted-foreground'
                  )}>
                    {item.description}
                  </p>
                )}
              </div>

              {/* Selection indicator */}
              {mode === 'multi' && (
                <div
                  className={cn(
                    'flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors mt-0.5',
                    itemSelected
                      ? 'bg-primary-500 border-primary-500'
                      : 'border-border bg-background'
                  )}
                >
                  {itemSelected && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              )}
              {mode === 'single' && (
                <div
                  className={cn(
                    'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors mt-0.5',
                    itemSelected
                      ? 'border-primary-500'
                      : 'border-border'
                  )}
                >
                  {itemSelected && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
